import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getFormattedDateTime } from "@/utils";
import { cn } from "@/lib/utils";
import { apiErrorMessage } from "@/api/perkpoint/client";
import {
  RULE_STATUS_LABELS,
  RulesApi,
  ruleFormValues,
  ruleKeys,
  type ApiRule,
  type ApiRuleVersion,
  type RuleAction,
} from "@/api/perkpoint/rules";
import { CreateRuleForm } from "./CreateRuleForm";
import { EVENT_LABELS, PRODUCT_OPTIONS, readEventEarn, releaseLabel } from "./eventEarn";

interface ViewRuleModalProps {
  rule: ApiRule;
  programId: string;
  isOpen: boolean;
  startInEdit?: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export const RULE_STATUS_STYLES: Record<ApiRule["status"], string> = {
  draft: "bg-slate-100 text-slate-700",
  pending_approval: "bg-amber-50 text-amber-800",
  active: "bg-green-50 text-green-700",
  paused: "bg-orange-50 text-orange-700",
  archived: "bg-slate-100 text-slate-500",
};

type ActionOption = { action: RuleAction; label: string; tone?: "primary" | "danger" };

/** Lifecycle actions offered for a rule in its current state. */
export function ruleActions(rule: ApiRule): ActionOption[] {
  const actions: ActionOption[] = [];
  const awaiting = rule.awaiting_approval || rule.status === "pending_approval";
  const hasDraft = rule.status === "draft" || (rule.has_pending_changes && !awaiting);
  if (awaiting) {
    actions.push({ action: "approve", label: "Approve", tone: "primary" });
    actions.push({ action: "reject", label: "Reject", tone: "danger" });
  } else if (hasDraft) {
    actions.push({ action: "submit", label: "Submit for approval", tone: "primary" });
    actions.push({ action: "publish", label: "Publish now" });
  }
  if (rule.status === "active") actions.push({ action: "pause", label: "Pause" });
  if (rule.status === "paused") actions.push({ action: "resume", label: "Resume", tone: "primary" });
  if (rule.status !== "archived") actions.push({ action: "archive", label: "Archive", tone: "danger" });
  return actions;
}

const ISSUANCE_LABELS: Record<string, string> = {
  fixed_per_txn: "Points per order",
  percent_of_invoice: "% of invoice amount",
};
const CRITERIA_LABELS: Record<string, string> = {
  percent_of_invoice: "% of invoice value",
  percent_of_points: "% of points balance",
  none: "No limit",
};
const EARN_WHILE_LABELS: Record<string, string> = {
  none: "None",
  partial: "Partial (cash part only)",
  full: "Full invoice",
};

function formatDate(value: string | null | undefined) {
  return value ? getFormattedDateTime(value) || "—" : "—";
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function VersionParts({ version }: { version: ApiRuleVersion }) {
  const eventEarn = readEventEarn(version);
  const tabs = [
    eventEarn ? { id: "earn", label: "Earn" } : null,
    version.redemption ? { id: "redemption", label: "Redemption" } : null,
    version.validity ? { id: "validity", label: "Validity" } : null,
  ].filter((tab): tab is { id: string; label: string } => tab !== null);
  const [tab, setTab] = useState(tabs[0]?.id ?? "earn");
  if (tabs.length === 0) {
    return <p className="text-sm text-muted-foreground">No parts set.</p>;
  }
  const value = tabs.some((t) => t.id === tab) ? tab : tabs[0].id;

  return (
    <Tabs value={value} onValueChange={setTab}>
      <TabsList
        className={cn(
          "grid w-full",
          tabs.length === 1 ? "grid-cols-1" : tabs.length === 2 ? "grid-cols-2" : "grid-cols-3",
        )}
      >
        {tabs.map((t) => (
          <TabsTrigger key={t.id} value={t.id}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {eventEarn ? (
        <TabsContent value="earn" className="mt-4">
          <div className="grid grid-cols-2 gap-4 rounded-md bg-muted p-4">
            <Field label="Runs when">
              {EVENT_LABELS[eventEarn.triggerEvent] || eventEarn.triggerEvent}
            </Field>
            <Field label="How points are calculated">
              {eventEarn.pointsIssuanceType === "PointsPerSpend"
                ? "Points per 100"
                : eventEarn.pointsIssuanceType === "Multiplier"
                  ? "Bonus multiplier"
                  : ISSUANCE_LABELS[
                      eventEarn.pointsIssuanceType === "%OfInvoiceAmount"
                        ? "percent_of_invoice"
                        : "fixed_per_txn"
                    ]}
            </Field>
            <Field
              label={
                eventEarn.pointsIssuanceType === "%OfInvoiceAmount"
                  ? "Percent of invoice"
                  : eventEarn.pointsIssuanceType === "PointsPerSpend"
                    ? "Points per 100"
                    : eventEarn.pointsIssuanceType === "Multiplier"
                      ? "Multiplier"
                      : "Points per order"
              }
            >
              {eventEarn.awardPoints}
              {eventEarn.pointsIssuanceType === "%OfInvoiceAmount"
                ? "%"
                : eventEarn.pointsIssuanceType === "Multiplier"
                  ? "×"
                  : " pts"}
            </Field>
            {eventEarn.pointsIssuanceType === "Multiplier" ? null : (
              <Field label="Minimum invoice">{eventEarn.minInvoiceAmount}</Field>
            )}
            <Field label="Applies to">
              {PRODUCT_OPTIONS.find((option) => option.value === eventEarn.productType)?.label
                ?? eventEarn.productType}
            </Field>
            <Field label="Points become available">
              {releaseLabel(eventEarn.release, eventEarn.releaseDays)}
            </Field>
          </div>
          {eventEarn.tierCredits.length ? (
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Also adds tier credit for {eventEarn.tierCredits.join(" and ")}.
            </p>
          ) : null}
        </TabsContent>
      ) : null}
      {version.redemption ? (
        <TabsContent value="redemption" className="mt-4">
          <div className="grid grid-cols-2 gap-4 rounded-md bg-muted p-4">
            <Field label="Max points on one order">{version.redemption.max_points_per_order}</Field>
            <Field label="Minimum invoice to redeem">
              {version.redemption.min_invoice_amount ?? "—"}
            </Field>
            <Field label="Usage limit">
              {CRITERIA_LABELS[version.redemption.criteria] ?? version.redemption.criteria}
              {version.redemption.criteria !== "none" && version.redemption.criteria_pct != null
                ? ` · ${version.redemption.criteria_pct}%`
                : ""}
            </Field>
            <Field label="Earn while redeem">
              {EARN_WHILE_LABELS[version.redemption.earn_while_redeem] ??
                version.redemption.earn_while_redeem}
            </Field>
          </div>
        </TabsContent>
      ) : null}
      {version.validity ? (
        <TabsContent value="validity" className="mt-4">
          <div className="grid grid-cols-2 gap-4 rounded-md bg-muted p-4">
            <Field label="Validity type">
              {version.validity.type === "revolving" ? "Revolving" : "Fixed"}
            </Field>
            <Field label="Points stay valid">{version.validity.days} days</Field>
          </div>
        </TabsContent>
      ) : null}
    </Tabs>
  );
}

/** Runs a lifecycle action (submit, approve, publish…) and refreshes rule lists. */
export function useRuleAction(programId: string, onDone?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rule, action, reason }: { rule: ApiRule; action: RuleAction; reason?: string }) =>
      RulesApi.action(programId, rule.id, action, reason),
    onSuccess: (updated, { action }) => {
      queryClient.invalidateQueries({ queryKey: ruleKeys.all });
      const verb: Record<RuleAction, string> = {
        submit: "sent for approval",
        approve: "approved and live",
        reject: "rejected",
        publish: "published",
        pause: "paused",
        resume: "resumed",
        archive: "archived",
      };
      const overlaps = action === "submit" ? updated.overlaps ?? [] : [];
      toast.success(`${updated.code} ${verb[action]}`, {
        description: overlaps.length
          ? `Runs alongside ${overlaps.map((o) => o.code).join(", ")}. Check they should stack.`
          : undefined,
      });
      onDone?.();
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not update the rule")),
  });
}

export function ViewRuleModal({
  rule,
  programId,
  isOpen,
  startInEdit = false,
  onClose,
  onUpdate,
}: ViewRuleModalProps) {
  const [isEditMode, setIsEditMode] = useState(startInEdit);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    setIsEditMode(startInEdit);
    setRejecting(false);
    setReason("");
  }, [rule.id, startInEdit, isOpen]);

  const detailQuery = useQuery({
    queryKey: ruleKeys.detail(programId, rule.id),
    queryFn: () => RulesApi.get(programId, rule.id),
    enabled: isOpen && Boolean(programId),
  });
  const current = detailQuery.data ?? rule;
  const actionMutation = useRuleAction(programId, () => {
    setRejecting(false);
    setReason("");
    detailQuery.refetch();
    onUpdate?.();
  });

  if (isEditMode) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Update rule {current.code}</DialogTitle>
          </DialogHeader>
          {current.status === "active" ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
              This rule is live. Saving creates a new draft. The live version keeps running until
              the draft is approved.
            </p>
          ) : null}
          <CreateRuleForm
            editMode
            initialValues={ruleFormValues(current)}
            onSuccess={() => {
              setIsEditMode(false);
              detailQuery.refetch();
              onUpdate?.();
            }}
            onCancel={() => setIsEditMode(false)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  const live = current.live_version;
  const draft = current.draft_version;
  const actions = ruleActions(current);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-8">
            <DialogTitle className="min-w-0 truncate">
              {current.code} · {current.name}
            </DialogTitle>
            {current.status !== "archived" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditMode(true)}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            ) : null}
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 rounded-md bg-muted p-4">
            <Field label="Status">
              <span
                className={cn(
                  "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                  RULE_STATUS_STYLES[current.status],
                )}
              >
                {RULE_STATUS_LABELS[current.status]}
              </span>
            </Field>
            <Field label="Live version">{live ? `v${live.version}` : "Not live yet"}</Field>
            <Field label="Created">{formatDate(current.created_at)}</Field>
            <Field label="Updated">{formatDate(current.updated_at)}</Field>
            {current.usage ? (
              <>
                <Field label="Transactions">
                  {current.usage.transactions.toLocaleString("en-IN")}
                </Field>
                <Field label="Points issued">
                  {current.usage.points_issued.toLocaleString("en-IN")}
                </Field>
              </>
            ) : null}
          </div>

          {draft && current.has_pending_changes ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">
                Draft v{draft.version}{" "}
                {current.awaiting_approval ? "· awaiting approval" : "· not submitted"}
              </h3>
              {draft.rejection_reason ? (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
                  Rejected: {draft.rejection_reason}
                </p>
              ) : null}
              <VersionParts version={draft} />
            </div>
          ) : null}

          {live ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Live v{live.version}</h3>
              <VersionParts version={live} />
            </div>
          ) : draft && !current.has_pending_changes ? (
            <VersionParts version={draft} />
          ) : null}

          {rejecting ? (
            <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-medium text-red-900">Why reject this draft?</p>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (required)"
                className="bg-white"
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" className="bg-white" onClick={() => setRejecting(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-red-600 text-white hover:bg-red-700"
                  disabled={!reason.trim() || actionMutation.isPending}
                  onClick={() =>
                    actionMutation.mutate({ rule: current, action: "reject", reason: reason.trim() })
                  }
                >
                  Reject draft
                </Button>
              </div>
            </div>
          ) : null}

          {actions.length && !rejecting ? (
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
              {actions.map(({ action, label, tone }) => (
                <Button
                  key={action}
                  size="sm"
                  variant={tone === "primary" ? "default" : "outline"}
                  className={cn(
                    tone === "primary" && "bg-emerald-700 text-white hover:bg-emerald-800",
                    tone === "danger" && "bg-white text-red-600 hover:text-red-700",
                    !tone && "bg-white",
                  )}
                  disabled={actionMutation.isPending}
                  onClick={() =>
                    action === "reject"
                      ? setRejecting(true)
                      : actionMutation.mutate({ rule: current, action })
                  }
                >
                  {label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
